import { useState, useEffect } from 'react';
import CardSkeleton from '@/components/shared/CardSkeleton';
import { Close } from '@mui/icons-material';
import {
	IconButton,
	Dialog,
	DialogActions,
	DialogContent,
	Button,
	DialogTitle,
	CircularProgress,
	Typography,
	Switch,
	FormControlLabel
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import { formatCurrency, formatDateWithMoment } from '../utilities/helpers/helpers';
import {
	useCancelSubscriptionMutation,
	useGetInvoicesListQuery,
	useGetActiveSubscriptionQuery
} from '@/store/services/subscriptions';
import useGetCurrentPlan from '../utilities/hooks/useGetCurrentPlan';
import InvoicesTable from '@/components/shared/InvoicesTable';

const PER_PAGE = 10;

function SubscriptionsPage() {
	const navigate = useNavigate();
	const [cursorStack, setCursorStack] = useState<(string | null)[]>([null]);
	const [currentPage, setCurrentPage] = useState(1);

	const { data: activeSubscriptionResponse, isLoading: isActiveSubscriptionLoading, isFetching: isActiveSubscriptionFetching } =
		useGetActiveSubscriptionQuery(undefined);
	const { data: invoiceResponse, isLoading: isInvoiceLoading, isFetching: isInvoiceFetching } = useGetInvoicesListQuery(
		{
			starting_after: cursorStack[currentPage - 1] ?? undefined,
			per_page: PER_PAGE
		},
		{
			refetchOnMountOrArgChange: true
		}
	);
	const [cancelSubscription, { isLoading: cancelSubscriptionLoading }] = useCancelSubscriptionMutation();
	const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
	const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(true);
	const { currentPlanInfo, isLoading: isCurrentPlanLoading } = useGetCurrentPlan();

	const invoices = invoiceResponse?.data?.invoices || [];
	const pagination = invoiceResponse?.data?.pagination;

	// Store next_cursor for the next page when we receive a response
	useEffect(() => {
		if (!pagination?.next_cursor) return;
		setCursorStack((prev) => {
			const next = [...prev];
			if (next.length <= currentPage) {
				next.length = currentPage + 1;
			}
			next[currentPage] = pagination.next_cursor;
			return next;
		});
	}, [pagination?.next_cursor, currentPage]);

	const pageCount = Math.max(
		currentPage,
		pagination?.has_more ? currentPage + 1 : currentPage
	);

	const handlePageChange = (page: number) => {
		if (page < 1 || page > pageCount) return;
		setCurrentPage(page);
	};

	// Update plan cookie to empty object when currentPlanInfo is null
	useEffect(() => {
		if (!isCurrentPlanLoading && currentPlanInfo === null) {
			// Update plan in localStorage instead of cookies
			import('@/utils/localStorage').then(({ setPlan }) => {
				setPlan({});
			})
				.then(() => {
					// Refresh handled automatically
					// window.location.reload();
				})
				.catch((error) => {
					console.error('Failed to update plan cookie:', error);
				});
		}
	}, [currentPlanInfo, isCurrentPlanLoading, navigate]);

	const handleCloseDialog = () => {
		setIsCancelDialogOpen(false);
		setCancelAtPeriodEnd(true);
	};

	const handleCancelSubscription = async () => {
		if (!activeSubscriptionResponse?.data?.id) {
			enqueueSnackbar('Subscription ID not found', {
				variant: 'error'
			});
			return;
		}

		try {
			const resp = await cancelSubscription({
				subscriptionId: activeSubscriptionResponse.data.id,
				cancelAtPeriodEnd
			}).unwrap();

			handleCloseDialog();

			enqueueSnackbar(
				(resp as { message?: string })?.message ||
					(resp as { data?: { message?: string } })?.data?.message ||
					'Subscription cancelled successfully.',
				{
					variant: 'success'
				}
			);

			navigate('/apps/billing/plans', { replace: true });
		} catch (error) {
			const typedError = error as {
				data?: { errors?: { detail?: string }[]; message?: string };
			};
			enqueueSnackbar(
				typedError?.data?.errors?.[0]?.detail ||
					typedError?.data?.message ||
					'An error occurred',
				{
					variant: 'error'
				}
			);
		}
	};

	const activeSubscription = activeSubscriptionResponse?.data;

	// Check if the button should be hidden
	const shouldHideUpgradeButton =
		currentPlanInfo?.interval === 'Yearly' && currentPlanInfo?.nickname === 'Assets ID Pro';

	return (
		<>
			<div className="flex flex-col min-w-0 flex-auto">
				<div className="flex-1">
					<div className="max-w-6xl mx-auto space-y-6">
						{/* Active Subscription Section */}
						{(isActiveSubscriptionLoading || isActiveSubscriptionFetching) ? (
							<CardSkeleton lines={5} />
						) : activeSubscription ? (
							<div className="bg-white rounded-lg shadow-sm p-6">
								<div className="flex items-center justify-between mb-4">
									<Typography
										variant="h6"
										className="text-2xl text-gray-900 capitalize"
										fontWeight="700"
									>
										Active Subscription:
									</Typography>
									{!shouldHideUpgradeButton && (
										<Button
											variant="contained"
											color="secondary"
											onClick={() => navigate('/apps/billing/plans')}
										>
											Upgrade Plan
										</Button>
									)}
								</div>
								<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4">
									<div>
										<Typography
											variant="body2"
											className="text-gray-500 mb-1"
										>
											Plan Name:
										</Typography>
										<Typography
											variant="h6"
											className="text-xl text-gray-900 capitalize"
											fontWeight="600"
										>
											{activeSubscription.plan?.name || activeSubscription.metadata?.plan_name}
										</Typography>
									</div>
									<div>
										<Typography
											variant="body2"
											className="text-gray-500 mb-1"
										>
											Price:
										</Typography>
										<Typography
											variant="h6"
											className="font-semibold text-gray-900"
										>
											{formatCurrency(
												activeSubscription.plan?.currency || 'USD',
												parseFloat(activeSubscription.plan?.price || '0')
											)}
											<span className="text-sm font-normal text-gray-500">
												/{activeSubscription.plan?.frequency}
											</span>
										</Typography>
									</div>
									<div>
										<Typography
											variant="body2"
											className="text-gray-500 mb-1"
										>
											{activeSubscription.is_renewable ? 'Next Billing Date:' : 'Ends On:'}
										</Typography>
										<Typography
											variant="body1"
											className="font-medium text-gray-900"
										>
											{formatDateWithMoment(activeSubscription.current_period_end)}
										</Typography>
									</div>
									<div>
										<Typography
											variant="body2"
											className="text-gray-500 mb-1"
										>
											Status:
										</Typography>
										<span
											className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold capitalize ${
												activeSubscription.status === 'active'
													? 'bg-green-100 text-green-800'
													: 'bg-red-100 text-red-800'
											}`}
										>
											{activeSubscription.status}
										</span>
									</div>
									<div>
										<Typography
											variant="body2"
											className="text-gray-500 mb-1"
										>
											Auto-Renewal:
										</Typography>
										<span
											className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold capitalize ${
												activeSubscription.is_renewable
													? 'bg-green-100 text-green-800'
													: 'bg-red-100 text-red-800'
											}`}
										>
											{activeSubscription.is_renewable ? 'ON' : 'OFF'}
										</span>
									</div>
								</div>
								{activeSubscription.is_renewable && (
									<div className="mt-4 pt-4 border-t border-gray-200">
										<Button
											variant="outlined"
											color="error"
											onClick={() => setIsCancelDialogOpen(true)}
										>
											Cancel Subscription
										</Button>
									</div>
								)}
							</div>
						) : (
							<div className="bg-white rounded-lg shadow-sm p-12 text-center">
								<Typography
									variant="body1"
									className="text-gray-600 mb-4"
								>
									No active subscription found.
								</Typography>
							</div>
						)}

						{/* All Invoices Table */}
						<div className="pb-4">
							<InvoicesTable
								invoices={invoices}
								isLoading={isInvoiceLoading}
								isFetching={isInvoiceFetching}
								pagination={pagination}
								page={currentPage}
								pageCount={pageCount}
								onPageChange={handlePageChange}
							/>
						</div>
					</div>
				</div>
			</div>

			{/* Cancel Subscription Dialog */}
			<Dialog
				open={isCancelDialogOpen}
				onClose={handleCloseDialog}
				maxWidth="sm"
				fullWidth
			>
				<IconButton
					className="popupCloseIcon"
					onClick={handleCloseDialog}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						zIndex: 1
					}}
				>
					<Close />
				</IconButton>
				<DialogTitle className="popupTitle">Cancel Subscription</DialogTitle>
				<DialogContent className="popupContent">
					<Typography
						variant="body1"
						className="mb-4"
					>
						Are you sure you want to cancel the subscription for the plan{' '}
						<span className="font-bold">
							{activeSubscription?.plan?.name || activeSubscription?.metadata?.plan_name}
						</span>{' '}
						?
					</Typography>
					<FormControlLabel
						control={
							<Switch
								checked={cancelAtPeriodEnd}
								onChange={(e) => setCancelAtPeriodEnd(e.target.checked)}
								color="primary"
							/>
						}
						label={
							<Typography variant="body2">
								{cancelAtPeriodEnd ? 'Auto-Renewal is off' : 'Cancel the subscription immediately.'}
							</Typography>
						}
					/>
				</DialogContent>
				<DialogActions sx={{ pb: 2 }}>
					<Button
						onClick={handleCloseDialog}
						variant="outlined"
						className="popupActionBtn btnGrey"
						disabled={cancelSubscriptionLoading}
					>
						Cancel
					</Button>
					<Button
						onClick={handleCancelSubscription}
						variant="contained"
						className="popupActionBtn"
						color="error"
						disabled={cancelSubscriptionLoading}
						startIcon={cancelSubscriptionLoading ? <CircularProgress size={14} /> : null}
					>
						Confirm Cancellation
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}

export default SubscriptionsPage;
